import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, ArrowRight, Calendar, Sparkles, TrendingUp, X, Search, Megaphone } from "lucide-react";

/**
 * PricelistChangesPanel — section « Annonces officielles Fortinet ».
 *
 * UX redesign (2026-02) : 4 cartes KPI cliquables qui pilotent un seul
 * panneau central (tab pattern). Au lieu de 4 accordéons à dérouler un par
 * un, l'utilisateur sélectionne une catégorie via une carte (EOS / EOL /
 * Successeurs plus chers / Nouveautés) et la table correspondante s'affiche
 * en dessous avec une barre de recherche dédiée. La carte active est mise en
 * évidence avec un anneau coloré et une animation de pulsation discrète.
 */

const TABS = [
  {
    id: "eos",
    label: "Sortants",
    fullLabel: "Sortie programmée (EOS)",
    icon: Calendar,
    accent: "amber",
    description: "Modèles dont la sortie de catalogue est annoncée par Fortinet.",
  },
  {
    id: "eol",
    label: "Fin de vie",
    fullLabel: "Fin de vie (EOL)",
    icon: X,
    accent: "zinc",
    description: "Produits déclarés EOL — ne plus proposer en nouveau projet.",
  },
  {
    id: "price",
    label: "Plus chers",
    fullLabel: "Successeurs plus chers",
    icon: TrendingUp,
    accent: "rose",
    description: "Successeurs G-series dont le tarif dépasse leur prédécesseur.",
  },
  {
    id: "new",
    label: "Nouveautés",
    fullLabel: "Nouveautés G-series",
    icon: Sparkles,
    accent: "emerald",
    description: "Nouveaux modèles ajoutés à la price list ce trimestre.",
  },
];

// Tailwind classes per accent — inline so the CSS purger picks them up.
const ACCENT = {
  amber: {
    cardBorder: "border-amber-200",
    cardBg: "bg-amber-50/40",
    activeRing: "ring-2 ring-amber-500 ring-offset-2",
    iconBg: "bg-amber-100",
    iconText: "text-amber-700",
    badge: "bg-amber-500",
    pillBg: "bg-amber-100",
    pillText: "text-amber-800",
    headerBar: "from-amber-500 to-amber-300",
  },
  zinc: {
    cardBorder: "border-zinc-200",
    cardBg: "bg-zinc-50",
    activeRing: "ring-2 ring-zinc-700 ring-offset-2",
    iconBg: "bg-zinc-200",
    iconText: "text-zinc-700",
    badge: "bg-zinc-700",
    pillBg: "bg-zinc-200",
    pillText: "text-zinc-800",
    headerBar: "from-zinc-700 to-zinc-400",
  },
  rose: {
    cardBorder: "border-rose-200",
    cardBg: "bg-rose-50/40",
    activeRing: "ring-2 ring-[#D42B1E] ring-offset-2",
    iconBg: "bg-rose-100",
    iconText: "text-rose-700",
    badge: "bg-[#D42B1E]",
    pillBg: "bg-rose-100",
    pillText: "text-rose-800",
    headerBar: "from-[#D42B1E] to-rose-300",
  },
  emerald: {
    cardBorder: "border-emerald-200",
    cardBg: "bg-emerald-50/40",
    activeRing: "ring-2 ring-emerald-500 ring-offset-2",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-700",
    badge: "bg-emerald-500",
    pillBg: "bg-emerald-100",
    pillText: "text-emerald-800",
    headerBar: "from-emerald-500 to-emerald-300",
  },
};

function formatUSD(v) {
  if (v == null || v === 0) return "—";
  return v.toLocaleString("fr-FR", { maximumFractionDigits: 0 });
}

export default function PricelistChangesPanel({ embedded = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Null = no card active → no table shown. Click a card to expand it,
  // click it again (or another card) to collapse / switch.
  const [activeTab, setActiveTab] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api
      .get("/pricelist/changes")
      .then((r) => setData(r.data))
      .catch(() => setError("Indisponible"));
  }, []);

  const rowsByTab = useMemo(() => {
    if (!data) return { eos: [], eol: [], price: [], new: [] };
    return {
      eos: data.eos || [],
      eol: data.eol || [],
      price: data.price_increases || [],
      new: (data.new_products || []).filter((r) => r.ptype === "HW" && !r.sku.includes("BDL")),
    };
  }, [data]);

  const counts = {
    eos: rowsByTab.eos.length,
    eol: rowsByTab.eol.length,
    price: rowsByTab.price.length,
    new: rowsByTab.new.length,
  };

  const activeMeta = activeTab ? TABS.find((t) => t.id === activeTab) : null;
  const activeAccent = activeMeta ? ACCENT[activeMeta.accent] : null;

  const visibleRows = useMemo(() => {
    if (!activeTab) return [];
    const rows = rowsByTab[activeTab] || [];
    if (!filter) return rows;
    const q = filter.trim().toLowerCase();
    return rows.filter((r) =>
      [r.sku, r.family, r.replacement?.sku, r.removal_quarter]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [activeTab, filter, rowsByTab]);

  if (error) return null;

  const Wrapper = embedded ? "div" : "section";
  const wrapperClass = embedded
    ? "border border-zinc-200 bg-white rounded-sm overflow-hidden"
    : "relative bg-zinc-50 border-t border-zinc-200";
  const innerClass = embedded ? "" : "max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12";

  return (
    <Wrapper className={wrapperClass} data-testid="pricelist-changes-section">
      <div className={innerClass}>
        {/* HERO HEADER — accent bar + megaphone icon, more striking than dry text */}
        <div className="relative">
          <div className="h-1 bg-gradient-to-r from-[#D42B1E] via-amber-400 to-emerald-400" />
          <div className="px-5 sm:px-6 py-5 bg-gradient-to-br from-zinc-900 to-zinc-800 text-white">
            <div className="flex items-start gap-4">
              <div className="shrink-0 h-10 w-10 sm:h-12 sm:w-12 rounded-sm bg-[#D42B1E]/15 border border-[#D42B1E]/30 flex items-center justify-center">
                <Megaphone className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6B6B]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#FF6B6B] bg-[#D42B1E]/15 border border-[#D42B1E]/30 px-2 py-0.5 rounded-sm">
                    Annonces officielles
                  </span>
                  <span className="text-[10px] font-mono text-zinc-400 uppercase tracking-wide">
                    Source · Fortinet HQ
                  </span>
                </div>
                <h2 className="mt-2 text-xl sm:text-2xl font-bold tracking-tight">
                  Évolutions du catalogue Fortinet
                </h2>
                <p className="mt-1.5 text-xs sm:text-sm text-zinc-300 max-w-3xl">
                  EOL, EOS, successeurs G-series et nouveautés annoncés par Fortinet sur la price
                  list courante. Cliquez sur une carte pour explorer la liste correspondante.
                </p>
                {data?.summary?.pricelist_filename && (
                  <p className="mt-1.5 text-[10px] text-zinc-400 font-mono break-all">
                    {data.summary.pricelist_filename}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* INTERACTIVE KPI CARDS — they double as tab triggers */}
        <div className="px-5 sm:px-6 py-5 sm:py-6 bg-white border-b border-zinc-200">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pricelist-changes-summary">
            {TABS.map((t) => {
              const isActive = activeTab === t.id;
              const accent = ACCENT[t.accent];
              const Icon = t.icon;
              const cnt = counts[t.id];
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    // Toggle: re-clicking the active card collapses it.
                    setActiveTab((cur) => (cur === t.id ? null : t.id));
                    setFilter("");
                  }}
                  className={[
                    "group text-left p-4 rounded-sm border-2 transition-all duration-200",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zinc-700",
                    isActive
                      ? `${accent.cardBorder} ${accent.cardBg} shadow-sm scale-[1.02]`
                      : "border-zinc-200 bg-white hover:border-zinc-400 hover:shadow-sm hover:-translate-y-0.5",
                  ].join(" ")}
                  data-testid={`pricelist-changes-tab-${t.id}`}
                  aria-pressed={isActive}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className={`h-9 w-9 rounded-sm inline-flex items-center justify-center ${accent.iconBg}`}>
                      <Icon className={`h-4 w-4 ${accent.iconText}`} />
                    </div>
                    {isActive && (
                      <span className={`h-2 w-2 rounded-full ${accent.badge} animate-pulse`} aria-hidden />
                    )}
                  </div>
                  <p className="mt-3 text-3xl font-bold tabular-nums leading-none text-zinc-900">
                    {data ? cnt.toLocaleString("fr-FR") : "…"}
                  </p>
                  <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                    {t.label}
                  </p>
                  <p className="mt-2 text-xs text-zinc-500 line-clamp-2">{t.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* ACTIVE PANEL — only rendered when the user clicked a card.
            Default state shows just the 4 KPI tiles, keeping the page short. */}
        {activeTab && activeMeta && (
        <div className="bg-white">
          <div className={`h-1 bg-gradient-to-r ${activeAccent.headerBar}`} />
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
            <div className={`h-8 w-8 rounded-sm inline-flex items-center justify-center ${activeAccent.iconBg}`}>
              <activeMeta.icon className={`h-4 w-4 ${activeAccent.iconText}`} />
            </div>
            <h3 className="text-sm font-semibold tracking-[0.12em] uppercase text-zinc-900">
              {activeMeta.fullLabel}
            </h3>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${activeAccent.pillBg} ${activeAccent.pillText}`}>
              {visibleRows.length.toLocaleString("fr-FR")}
              {filter && rowsByTab[activeTab].length !== visibleRows.length && (
                <span className="opacity-70"> / {rowsByTab[activeTab].length.toLocaleString("fr-FR")}</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => { setActiveTab(null); setFilter(""); }}
              className="ml-auto sm:ml-0 text-[11px] text-zinc-500 hover:text-[#D42B1E] inline-flex items-center gap-1"
              data-testid="pricelist-changes-collapse"
              title="Replier le tableau"
            >
              <X className="h-3 w-3" /> Fermer
            </button>
            <div className="relative w-full sm:w-72 sm:ml-auto">
              <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filtrer (SKU, famille, remplaçant…)"
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-zinc-300 rounded-sm focus:outline-none focus:border-[#D42B1E]"
                data-testid="pricelist-changes-filter"
              />
            </div>
          </div>

          {/* Table or empty/loading state */}
          {!data ? (
            <div className="p-12 text-center text-sm text-zinc-500">Chargement des annonces Fortinet…</div>
          ) : visibleRows.length === 0 ? (
            <div className="p-12 text-center" data-testid={`pricelist-changes-empty-${activeTab}`}>
              <AlertTriangle className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
              <p className="text-sm text-zinc-600 font-medium">
                {filter
                  ? "Aucune référence ne correspond à votre filtre."
                  : "Aucune annonce dans cette catégorie."}
              </p>
              {filter && (
                <button
                  type="button"
                  onClick={() => setFilter("")}
                  className="mt-2 text-xs text-[#D42B1E] hover:underline"
                >
                  Effacer le filtre
                </button>
              )}
            </div>
          ) : (
            <ChangesTable tabId={activeTab} rows={visibleRows} />
          )}
        </div>
        )}

        {/* Helper hint when no card is active */}
        {!activeTab && (
          <div className="px-5 sm:px-6 py-6 text-center bg-zinc-50/40 border-t border-zinc-100" data-testid="pricelist-changes-hint">
            <p className="text-xs text-zinc-500">
              Cliquez sur une carte ci-dessus pour afficher la liste détaillée correspondante.
            </p>
          </div>
        )}

        <p className="px-5 sm:px-6 py-4 text-[11px] text-zinc-500 leading-relaxed bg-zinc-50/60 border-t border-zinc-100">
          ⓘ Les remplaçants sont déduits par correspondance numérique entre générations
          (FG-100F → FG-120G, FG-200F → FG-200G…). Les hausses tarifaires comparent le prix
          du modèle sortant à celui de son remplaçant officiel listé. Pour les nuances de mapping,
          référez-vous toujours à la communication Fortinet officielle.
        </p>
      </div>
    </Wrapper>
  );
}

function ChangesTable({ tabId, rows }) {
  return (
    <div className="max-h-[460px] overflow-y-auto">
      <table className="w-full text-sm" data-testid={`pricelist-changes-table-${tabId}`}>
        <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10">
          <tr className="text-[10px] uppercase tracking-[0.1em] text-zinc-600">
            <th className="text-left px-4 py-2.5 font-semibold">SKU</th>
            <th className="text-left px-4 py-2.5 font-semibold">Famille</th>
            {tabId === "eos" && <th className="text-left px-4 py-2.5 font-semibold">Sortie</th>}
            {(tabId === "eos" || tabId === "price" || tabId === "new") && (
              <th className="text-right px-4 py-2.5 font-semibold">{tabId === "new" ? "Prix ($)" : "Prix actuel ($)"}</th>
            )}
            {tabId === "eol" && <th className="text-left px-4 py-2.5 font-semibold">Statut</th>}
            {(tabId === "eos" || tabId === "eol" || tabId === "price") && (
              <th className="text-left px-4 py-2.5 font-semibold">Remplaçant</th>
            )}
            {(tabId === "eos" || tabId === "price") && (
              <th className="text-right px-4 py-2.5 font-semibold">Δ Prix</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.slice(0, 200).map((r) => (
            <tr key={r.sku} className="hover:bg-zinc-50/60" data-testid={`pricelist-changes-row-${r.sku}`}>
              <td className="px-4 py-2.5 font-mono text-xs text-zinc-900 whitespace-nowrap">{r.sku}</td>
              <td className="px-4 py-2.5 text-zinc-600 truncate max-w-[260px]">{r.family || "—"}</td>
              {tabId === "eos" && (
                <td className="px-4 py-2.5">
                  <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 text-[11px] rounded-sm font-mono">
                    {r.removal_quarter}
                  </span>
                </td>
              )}
              {(tabId === "eos" || tabId === "price" || tabId === "new") && (
                <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-700 whitespace-nowrap">
                  {formatUSD(r.price)}
                </td>
              )}
              {tabId === "eol" && (
                <td className="px-4 py-2.5">
                  <span
                    className={`inline-block px-2 py-0.5 text-[11px] rounded-sm font-medium ${
                      r.in_pricelist ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-600"
                    }`}
                  >
                    {r.in_pricelist ? "Encore listé" : "Retiré"}
                  </span>
                </td>
              )}
              {(tabId === "eos" || tabId === "eol" || tabId === "price") && (
                <td className="px-4 py-2.5">
                  {r.replacement ? (
                    <div className="flex items-center gap-2">
                      <ArrowRight className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                      <span className="font-mono text-xs text-zinc-900">{r.replacement.sku}</span>
                      <span className="text-[11px] text-zinc-500">${formatUSD(r.replacement.price)}</span>
                    </div>
                  ) : (
                    <span className="text-[11px] text-zinc-400 italic">non défini</span>
                  )}
                </td>
              )}
              {(tabId === "eos" || tabId === "price") && (
                <td className="px-4 py-2.5 text-right">
                  {r.price_delta_pct != null ? (
                    <span
                      className={`inline-block px-2 py-0.5 text-[11px] rounded-sm font-mono font-semibold ${
                        r.price_direction === "up"
                          ? "bg-rose-50 text-rose-700"
                          : r.price_direction === "down"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-zinc-100 text-zinc-600"
                      }`}
                    >
                      {r.price_direction === "up" ? "+" : ""}
                      {r.price_delta_pct.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-[11px] text-zinc-400">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 200 && (
        <div className="px-4 py-2 text-center text-[11px] text-zinc-500 border-t border-zinc-100 bg-zinc-50">
          + {rows.length - 200} autres références — utilisez le filtre pour affiner.
        </div>
      )}
    </div>
  );
}
